import React from "react";
import { PropTypes } from "prop-types";
import { MrbCollapsiblePanel } from "mrb/components/collapsible-panel";
import { MrbEditFormLayout } from "mrb/components/layout";
import { MrbFieldSelect } from "mrb/components/select";
import { MrbDatePicker } from "mrb/components/date-picker";
import { MrbFieldInput, MrbFieldNumericInput, MrbNumericInput } from "mrb/components/input";
import { MrbFieldToggle } from "mrb/components/checkbox";
import { NakiScheduleFieldDatePicker } from "../../common/components";
import { MrbButton } from "mrb/components/button";
import { MrbFieldRadioButtons } from "mrb/components/radio";
import { MrbSelect } from "mrb/components/select";
import { ARTIST_FORM_STATUS } from "administration/naki-nigun/stores";
import { CoverImageUpload } from "common/components";
import { observer } from "mobx-react";

function NakiNigunAlbumEditTemplate({ nakiNigunAlbumEditViewStore, t }) {
    const {
        form,
        albumIndex,
        album,
        episodeSortTypeOptions,
        isCollapsed,
        toggleCollapse,
        setPanelRef,
        coverImageUploadViewStore,
        resetForm,
        deleteAlbum,
        genreSelectStore,
        releaseDatePickerValue,
        setReleaseDate,
        setReleaseYear,
        releaseDateMinDate,
        releaseDateMaxDate,
        producerRows,
        totalRevenue,
        addProducerRow,
        removeProducerRow,
        setProducerRowRevenue,
        overrideRadioUsersSettingsIsVisible,
        overrideAppUsersSettingsIsVisible,
        isPremiumDisabled,
        isActiveStatusDisabled,
        showScheduledRelease,
    } = nakiNigunAlbumEditViewStore;
    return (
        <React.Fragment>
            <MrbCollapsiblePanel
                innerRef={setPanelRef}
                title={album.title}
                t={t}
                isCollapsed={isCollapsed}
                onChangeCollapse={toggleCollapse}
                headerRender={(props) => (
                    <AlbumPanelHeader {...props} album={album} albumIndex={albumIndex} deleteAlbum={deleteAlbum} />
                )}
            >
                {() => (
                    <MrbEditFormLayout
                        form={form}
                        t={t}
                        onClickCancel={resetForm}
                        cancelLabel="Cancel"
                        submitLabel="Update"
                    >
                        <div className="row">
                            <div className="col col-sml-12 col-xxlrg-8 u-mar--bottom--sml">
                                <div className="row">
                                    <div className="col col-sml-12">
                                        <MrbFieldInput
                                            t={t}
                                            field={form.$("title")}
                                            classNameExtend="c-input--outline"
                                        />
                                    </div>

                                    <div className="col col-sml-12">
                                        <MrbFieldInput
                                            t={t}
                                            field={form.$("albumFeed.feedUrl")}
                                            classNameExtend="c-input--outline"
                                        />
                                    </div>

                                    <div className="col col-sml-12 col-med-6 col-xxlrg-4">
                                        <div className="c-form__field c-release-date">
                                            <label className="c-form__field__label">
                                                {t(form.$("releaseDate").label)}
                                            </label>
                                            <MrbDatePicker
                                                t={t}
                                                value={releaseDatePickerValue}
                                                onChange={setReleaseDate}
                                                dateFormat="d MMM yyyy"
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="scroll"
                                                scrollableYearDropdown
                                                yearDropdownItemNumber={200}
                                                onYearChange={setReleaseYear}
                                                minDate={releaseDateMinDate}
                                                maxDate={releaseDateMaxDate}
                                                isClearable
                                                todayButtonLabel=""
                                                placeholder={t(
                                                    "ADMINISTRATION.NAKI_NIGUN.ARTIST.EDIT.ALBUM.FIELDS.RELEASE_DATE_PLACEHOLDER"
                                                )}
                                                classNameOverride="c-input c-input--med c-input--outline"
                                            />
                                        </div>
                                    </div>

                                    <div className="col col-sml-12 col-med-6 col-xxlrg-8">
                                        <MrbFieldSelect
                                            t={t}
                                            store={genreSelectStore}
                                            classNameExtend="c-dropdown--dark"
                                        />
                                    </div>
                                    <div className="col col-sml-12">
                                        <label className="c-form__field__label">
                                            {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.EDIT.ALBUM.FIELDS.PRODUCER_LABEL")}
                                            {form.$("isPremium").value && (
                                                <span className="u-type--color--warning"> *</span>
                                            )}
                                        </label>
                                        {producerRows.map((row, index) => (
                                            <AlbumProducerRowItem
                                                key={index}
                                                row={row}
                                                index={index}
                                                removeRow={removeProducerRow}
                                                setRowRevenue={setProducerRowRevenue}
                                                t={t}
                                            />
                                        ))}
                                        <div className="u-mar--bottom--sml">
                                            <MrbButton
                                                classNameOverride="c-btn c-btn--sml c-btn--primary"
                                                label={t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.ADD_PRODUCER")}
                                                onClick={addProducerRow}
                                            />
                                        </div>
                                        {producerRows.length > 0 && (
                                            <div className="u-mar--bottom--sml">
                                                <p
                                                    className={`u-type--sml ${
                                                        Math.round(totalRevenue * 100) / 100 === 100
                                                            ? "u-type--color--success"
                                                            : "u-type--color--warning"
                                                    }`}
                                                >
                                                    {t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.TOTAL_REVENUE")}:{" "}
                                                    {Math.round(totalRevenue * 100) / 100}%
                                                    {Math.round(totalRevenue * 100) / 100 !== 100 && (
                                                        <span className="u-mar--left--sml">
                                                            (
                                                            {t(
                                                                "ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.VALIDATION.REVENUE_MUST_EQUAL_100"
                                                            )}
                                                            )
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="col col-sml-12">
                                        <MrbFieldInput
                                            t={t}
                                            field={form.$("keywords")}
                                            classNameExtend="c-input--outline"
                                        />
                                        <div className="u-display--flex u-display--flex--ai--center">
                                            <MrbFieldRadioButtons
                                                field={form.$("albumFeed.contentSortTypeId")}
                                                t={t}
                                                options={episodeSortTypeOptions}
                                            />
                                            <div className="col col-sml-12 col-med-6 col-xxlrg-2">
                                                <MrbFieldNumericInput
                                                    classNameOverride={"c-input c-input--sml c-input--outline"}
                                                    precision={2}
                                                    disabled={!form.$("isPremium").value}
                                                    t={t}
                                                    field={form.$("price")}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col col-sml-12 col-xxlrg-4">
                                <div className="c-toggle__card-list">
                                    <MrbFieldToggle
                                        classNameExtend="c-toggle__item--inline"
                                        t={t}
                                        field={form.$("isActive")}
                                        disabled={isActiveStatusDisabled}
                                    />
                                    {showScheduledRelease && (
                                        <div className="c-schedule-release">
                                            <div className="c-schedule-release__label">
                                                {t(form.$("scheduledReleaseAt").label)}
                                            </div>
                                            <NakiScheduleFieldDatePicker
                                                field={form.$("scheduledReleaseAt")}
                                                showLabel={false}
                                                t={t}
                                                showTimeSelect={true}
                                                timeIntervals={60}
                                                onChangeRaw={(e) => e.preventDefault()}
                                                dateFormat="MM/dd/yyyy h aa"
                                                minDate={new Date()}
                                                isClearable={true}
                                            />
                                        </div>
                                    )}
                                    <MrbFieldToggle
                                        classNameExtend="c-toggle__item--inline"
                                        t={t}
                                        field={form.$("isPremium")}
                                        disabled={isPremiumDisabled}
                                    />
                                    <MrbFieldToggle
                                        classNameExtend="c-toggle__item--inline"
                                        t={t}
                                        field={form.$("isTurnedOnByDefault")}
                                    />
                                    <div className="u-separator--primary u-mar--none"></div>

                                    {overrideRadioUsersSettingsIsVisible ? (
                                        <MrbFieldToggle
                                            classNameExtend="c-toggle__item--inline"
                                            t={t}
                                            field={form.$("overrideRadioUsersSettings")}
                                        />
                                    ) : null}
                                    <MrbFieldToggle
                                        classNameExtend="c-toggle__item--inline"
                                        t={t}
                                        field={form.$("isTurnedOnByDefaultInApp")}
                                    />
                                    <MrbFieldToggle
                                        classNameExtend="c-toggle__item--inline"
                                        t={t}
                                        field={form.$("isTurnedOnByDefaultInEmbeddedApp")}
                                    />
                                    {overrideAppUsersSettingsIsVisible ? (
                                        <MrbFieldToggle
                                            classNameExtend="c-toggle__item--inline"
                                            t={t}
                                            field={form.$("overrideAppUsersSettings")}
                                        />
                                    ) : null}
                                    <div className="u-separator--primary u-mar--none"></div>

                                    <MrbFieldToggle
                                        classNameExtend="c-toggle__item--inline"
                                        t={t}
                                        field={form.$("isFeatured")}
                                    />
                                    <MrbFieldToggle
                                        classNameExtend="c-toggle__item--inline"
                                        t={t}
                                        field={form.$("albumFeed.hlsEnabled")}
                                        disabled={isActiveStatusDisabled}
                                    />
                                </div>

                                <div className="u-separator--primary"></div>

                                <CoverImageUpload
                                    classNameExtendUpload="c-upload--tny"
                                    coverImageUploadViewStore={coverImageUploadViewStore}
                                    isFullWidthFooterButtons
                                    showInfo={false}
                                />
                            </div>
                        </div>

                        <div className="u-separator--primary"></div>
                    </MrbEditFormLayout>
                )}
            </MrbCollapsiblePanel>
        </React.Fragment>
    );
}

NakiNigunAlbumEditTemplate.propTypes = {
    nakiNigunAlbumEditViewStore: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const AlbumProducerRowItem = observer(function AlbumProducerRowItem({ row, index, removeRow, setRowRevenue, t }) {
    return (
        <div className="row row--align-end u-mar--bottom--sml">
            <div className="col col-sml-12 col-lrg-7 u-mar--bottom--sml">
                <MrbSelect store={row.producerSelectStore} classNameExtend="c-dropdown--dark" />
            </div>
            <div className="col col-sml-12 col-lrg-3 u-mar--bottom--sml">
                {row.revenuePercentage < 0.01 && (
                    <p className="u-type--sml u-type--color--warning">
                        {t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.VALIDATION.REVENUE_MIN")}
                    </p>
                )}
                <MrbNumericInput
                    className="c-input c-input--outline c-input--numeric c-input--numeric--base"
                    precision={2}
                    step={0.01}
                    t={t}
                    value={row.revenuePercentage}
                    min={0}
                    max={100}
                    onChange={(e) => setRowRevenue(index, e)}
                    strict={true}
                />
            </div>
            <div className="col col-sml-12 col-lrg-2 u-mar--bottom--sml">
                <div className="u-display--flex u-display--flex--ai--center" style={{ height: "38px" }}>
                    <MrbButton
                        classNameOverride="c-btn c-btn--icon"
                        onlyIcon
                        icon="u-icon u-icon--med u-icon--trash"
                        onClick={() => removeRow(index)}
                    />
                </div>
            </div>
        </div>
    );
});

const AlbumPanelHeader = observer(function AlbumPanelHeader({ title, t, isCollapsed, albumIndex, album, deleteAlbum }) {
    return (
        <React.Fragment>
            <div className="u-display--flex u-display--flex--ai--center">
                <i className="u-icon u-icon--med u-icon--drag u-mar--right--tny"></i>
                <span className="u-type--wgt--bold u-mar--right--tny">{albumIndex}.</span>
                {t(title)}
                {album.isPremium && (
                    <i className="u-icon u-icon--med u-icon--premium u-mar--left--tny u-mar--right--tny"></i>
                )}
            </div>
            <div className="u-display--flex u-display--flex--ai--center">
                {album.FORM_STATUS === ARTIST_FORM_STATUS.CHANGED && (
                    <div className="c-validation--info c-validation--info--secondary">
                        <p className="c-validation__label">
                            {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.EDIT.VALIDATION.ALBUM_CHANGED")}
                        </p>
                    </div>
                )}
                <div className="u-display--flex u-display--flex--ai--center">
                    <MrbButton
                        t={t}
                        label="ADMINISTRATION.NAKI_NIGUN.ARTIST.EDIT.ALBUM.BUTTONS.DELETE"
                        onClick={deleteAlbum}
                        classNameOverride="c-btn c-btn--sml c-btn--ghost u-mar--right--tny"
                    />
                    <i
                        className={`u-icon u-icon--med u-icon--arrow-down ${
                            isCollapsed ? "u-rotate--180" : ""
                        } u-cursor--pointer u-mar--top--micro--alt`}
                    ></i>
                </div>
            </div>
        </React.Fragment>
    );
});

export default observer(NakiNigunAlbumEditTemplate);
